import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import api from '@/services/apiClient';
import Select from '@/components/ui/Select';
import EmptyState from '@/components/ui/EmptyState';
import { fmtNum } from '@/lib/enums';

const METRICS = ['bestWeight', 'est1RM', 'volume'];

/** Evolução de carga/1RM/volume por exercício, a partir das sessões do aluno. */
export default function ExerciseProgressSection({ userId, MetricCard }) {
  const { t } = useTranslation();
  const [exercises, setExercises] = useState([]);
  const [exerciseId, setExerciseId] = useState('');
  const [metric, setMetric] = useState('bestWeight');
  const [data, setData] = useState(null);

  useEffect(() => {
    setExercises([]);
    setExerciseId('');
    setData(null);
    if (!userId) return;
    api
      .get('/sessions/exercises', { studentId: userId })
      .then((res) => {
        const list = (res.data || res).exercises || [];
        setExercises(list);
        if (list[0]) setExerciseId(String(list[0].id));
      })
      .catch(() => setExercises([]));
  }, [userId]);

  useEffect(() => {
    if (!exerciseId) return;
    setData(null);
    api
      .get(`/sessions/exercises/${exerciseId}/history`, { studentId: userId })
      .then((res) => setData(res.data || res))
      .catch(() => setData(null));
  }, [exerciseId, userId]);

  const chart = (data?.history || []).map((h) => ({
    d: (h.date || '').slice(5),
    v: h[metric],
  }));
  const prs = data?.prs || {};

  return (
    <div>
      <div className="kicker" style={{ marginBottom: 12 }}>{t('students.progress.title')}</div>
      {exercises.length === 0 ? (
        <EmptyState title={t('students.progress.emptyTitle')} description={t('students.progress.emptyDesc')} />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <Select
              value={exerciseId}
              onChange={(e) => setExerciseId(e.target.value)}
              options={exercises.map((ex) => ({ value: String(ex.id), label: `${ex.name} (${ex.sessionsCount})` }))}
              style={{ minWidth: 220 }}
            />
            <Select
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              options={METRICS.map((m) => ({ value: m, label: t(`students.progress.metric.${m}`) }))}
            />
          </div>
          <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
            <MetricCard label={t('students.progress.prWeight')} value={prs.maxWeight ? `${fmtNum(prs.maxWeight.value)}kg` : '—'} tone="accent" />
            <MetricCard label={t('students.progress.pr1rm')} value={prs.maxEst1RM ? `${fmtNum(prs.maxEst1RM.value)}kg` : '—'} tone="recovery" />
            <MetricCard label={t('students.progress.prVolume')} value={prs.maxVolume ? fmtNum(prs.maxVolume.value) : '—'} />
          </div>
          {chart.length < 2 ? (
            <EmptyState title={t('students.progress.fewSessions')} />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis dataKey="d" stroke="var(--low)" fontSize={11} />
                <YAxis stroke="var(--low)" fontSize={11} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ background: 'var(--surf2)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--hi)' }} />
                <Line type="monotone" dataKey="v" stroke="var(--volt)" strokeWidth={2} dot={false} name={t(`students.progress.metric.${metric}`)} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </>
      )}
    </div>
  );
}
